import type { ModuleKey } from '@fos/shared/enums';
import { useQuery } from '@tanstack/react-query';
import type { BadgeKind } from '../ui/Badge';
import { trpc } from './trpc';
import { useAccess } from './useAccess';

/**
 * Plakietki w menu (Inbox: nieprzeczytane — „Unread”, Akceptacje: do decyzji — „Attention”, Agenci: wyniki do
 * odbioru — „Muted”, Fala 5). Źródło danych podłącza
 * kompozycja Fali 3 (`features/composition`: `inbox.counts` + `approvals.counts`); tryb podglądu (`?mock=demo`) —
 * atrapa. Odświeżanie: co 60 s, po każdej mutacji (`MutationCache` w `app/providers.tsx`) i po akcjach modułów
 * (`NAV_BADGES_QUERY_KEY`).
 */
export type NavBadges = Partial<Record<ModuleKey, { kind: BadgeKind; count: number }>>;

type TrpcClient = ReturnType<typeof trpc.useUtils>['client'];

/** Kontekst źródła: klient tRPC i dostęp osoby (plakietka tylko dla modułu, który osoba widzi). */
export interface NavBadgesContext {
  client: TrpcClient;
  can: (module: ModuleKey) => boolean;
}

let source: (ctx: NavBadgesContext) => Promise<NavBadges> = async () => ({});

export function setNavBadgesSource(next: (ctx: NavBadgesContext) => Promise<NavBadges>): void {
  source = next;
}

export const NAV_BADGES_QUERY_KEY = ['nav', 'badges'] as const;

/** Odświeżanie plakietek (30–60 s, DESIGN §5). */
export const NAV_BADGES_REFRESH_MS = 60_000;

export function useNavBadges(): NavBadges {
  const utils = trpc.useUtils();
  const access = useAccess();
  // klucz zależy od modułów osoby (inny zestaw modułów = inne zapytania); prefiks NAV_BADGES_QUERY_KEY do odświeżania
  const modules = (['inbox', 'approvals', 'agents'] as const).filter((m) => access.can(m)).join(',');
  const { data } = useQuery({
    queryKey: [...NAV_BADGES_QUERY_KEY, modules],
    queryFn: () => source({ client: utils.client, can: (m) => access.can(m) }),
    staleTime: 30_000,
    refetchInterval: NAV_BADGES_REFRESH_MS,
  });
  return data ?? {};
}
